import {SupportedLanguageCode} from './dropdown-options';
import {Weight} from './questionnaire/weight';
import {POUNDS_PER_KILOGRAM} from './utils-const';

export type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

export const roundUpToNearestInterval = (value: number, interval: number): number => {
  return Math.ceil(value / interval) * interval;
};

export const isMultipleOf = (value: number, multiple: number): boolean => {
  return value % multiple === 0;
};

export const bmi = (heightInCm: number, weightInKg: number) => {
  const bmi = weightInKg / Math.pow(heightInCm / 100, 2);
  return Number(bmi.toFixed(1));
};

export const getWeightInKg = (weight: Weight) => {
  return weight.unit === 'lbs' ? weight.value / POUNDS_PER_KILOGRAM : weight.value;
};

export const percentage = (value: number, total: number) => {
  return (value / total) * 100;
};

export const formatPostalCode = (postalCode: string): string => {
  const firstPart = postalCode.slice(0, 3);
  const lastPart = postalCode.slice(3);
  return `${firstPart} ${lastPart}`.toUpperCase();
};

export const formatPhoneNumber = (phoneNumber: string): string => {
  return phoneNumber.replace(/([0-9]{3})([0-9]{3})([0-9]{4})/g, '($1) $2-$3');
};

export function snakeToCamel(name: string): string {
  return name.replace(/(_\w)/g, (m) => m[1].toUpperCase());
}

export function camelToSnake(name: string): string {
  return name.replace(/\.?([A-Z])/g, (m, x) => '_' + x.toLowerCase()).replace(/^_/, '');
}

export function snakeToSeparatedPascal(name: string): string {
  return name
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

// eslint-disable-next-line @typescript-eslint/ban-types
export function copyPropertiesAndTransform<T>(obj: any, transformer: Function, exception?: RegExp): T {
  if (Array.isArray(obj)) {
    return <any>obj.map((item) => copyPropertiesAndTransform(item, transformer, exception));
  } else if (obj != null && typeof obj === 'object' && !(obj instanceof Date)) {
    const newObj: any = {};
    for (const name in obj) {
      if (exception) {
        newObj[name.match(exception) ? name : transformer(name)] = copyPropertiesAndTransform(
          obj[name],
          transformer,
          exception,
        );
      } else {
        newObj[transformer(name)] = copyPropertiesAndTransform(obj[name], transformer, exception);
      }
    }
    return newObj;
  } else {
    return obj;
  }
}

export function keepPropertiesWithPrefixAndRemovePrefix(
  obj: any,
  prefix: string,
  shouldAffectNested = true,
): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => keepPropertiesWithPrefixAndRemovePrefix(item, prefix));
  } else if (obj != null && typeof obj === 'object' && !(obj instanceof Date)) {
    const newObj: any = {};
    for (const key in obj) {
      if (key.startsWith(prefix)) {
        const slicedKey = key.slice(prefix.length);
        newObj[slicedKey.charAt(0).toLowerCase() + slicedKey.slice(1)] = shouldAffectNested
          ? keepPropertiesWithPrefixAndRemovePrefix(obj[key], prefix)
          : obj[key];
      }
    }
    return newObj;
  } else {
    return obj;
  }
}

export function removeNullProperties(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => removeNullProperties(item));
  } else if (obj != null && typeof obj === 'object' && !(obj instanceof Date)) {
    const newObj: any = {};
    for (const key in obj) {
      if (
        obj[key] !== null &&
        obj[key] !== '' &&
        (typeof obj[key] !== 'string' || obj[key].trim() !== '') &&
        (typeof obj[key] !== 'object' || !isObjectEmpty(obj[key]))
      ) {
        newObj[key] = removeNullProperties(obj[key]);
      }
    }
    return newObj;
  } else {
    return obj;
  }
}

export const objectToJson = <T>(object: T): string => {
  return copyPropertiesAndTransform(object, camelToSnake);
};

export const jsonToObject = <T>(json: string): T => {
  return copyPropertiesAndTransform(json, snakeToCamel);
};

export const isObjectEmpty = (obj: object) => {
  if (obj == null) {
    return true;
  }

  if (obj instanceof Date) {
    return false;
  }

  if (Object.keys(obj).length < 1) {
    return true;
  }

  return Object.keys(obj).every((key) => (typeof obj[key] === 'object' ? isObjectEmpty(obj[key]) : false));
};

export const copyToClipboard = (value: string) => {
  const temporaryTextarea = createTemporarySelectionBox(value);
  document.body.appendChild(temporaryTextarea);

  temporaryTextarea.focus();
  temporaryTextarea.select();

  document.execCommand('copy');
  document.body.removeChild(temporaryTextarea);
};

const createTemporarySelectionBox = (value: string) => {
  const temporaryTextarea = document.createElement('textarea');
  temporaryTextarea.value = value;
  temporaryTextarea.style.position = 'fixed';
  temporaryTextarea.style.left = '0';
  temporaryTextarea.style.top = '0';
  temporaryTextarea.style.opacity = '0';

  return temporaryTextarea;
};

export const generatePermutations = (arrays: any[][]): any[] => {
  if (arrays.length === 1) {
    return arrays[0].map((item: any) => [item]);
  }

  const [firstArray, ...remainingArrays] = arrays;
  const permutations = generatePermutations(remainingArrays);
  const results: any[] = [];

  for (const item of firstArray) {
    for (const permutation of permutations) {
      results.push([item, ...permutation]);
    }
  }

  return results;
};

export function diff(obj1: object, obj2: object): object {
  const result: Record<string, any> = {};

  for (const key in obj1) {
    if (Object.prototype.hasOwnProperty.call(obj1, key) && !Object.prototype.hasOwnProperty.call(obj2, key)) {
      result[key] = obj1[key];
    } else if (!deepEqual(obj1[key], obj2[key])) {
      if (isJsonObject(obj1[key]) && isJsonObject(obj2[key])) {
        result[key] = diff(obj1[key], obj2[key]);
      } else {
        result[key] = obj2[key];
      }
    }
  }

  for (const key in obj2) {
    if (Object.prototype.hasOwnProperty.call(obj2, key) && !Object.prototype.hasOwnProperty.call(obj1, key)) {
      result[key] = obj2[key];
    }
  }

  return result;
}

export function isJsonObject(value: any): boolean {
  return !!value && Object.getPrototypeOf(value) === Object.prototype;
}

export function deepEqual(value1: any, value2: any): boolean {
  if (value1 === null || value2 === null) {
    return value1 === value2;
  }

  if (value1 instanceof Date && value2 instanceof Date) {
    return value1.getTime() === value2.getTime();
  }

  if (typeof value1 !== 'object' || typeof value2 !== 'object') {
    return value1 === value2;
  }

  const keys1 = Object.keys(value1);
  const keys2 = Object.keys(value2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(value1[key], value2[key])) {
      return false;
    }
  }

  return true;
}

export function copyObjectAndDropProperties<T>(obj: T, propertiesToDrop: string[]): T {
  if (obj === null || typeof obj !== 'object' || obj instanceof Date) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => copyObjectAndDropProperties(item, propertiesToDrop)) as any;
  }

  const copiedObj: any = {};
  for (const key in obj) {
    if (propertiesToDrop.includes(key)) {
      continue;
    }
    copiedObj[key] = copyObjectAndDropProperties(obj[key], propertiesToDrop);
  }

  return copiedObj;
}

export function isPhoneNumber(value: string) {
  const potentiallyPhoneNumber = value.replace(/\D/g, '');
  // length of phone number with (11) or without (10) Country Code
  return potentiallyPhoneNumber.length === 10 || potentiallyPhoneNumber.length === 11;
}

export function trimPhoneNumber(value: string): string {
  return value.replace('+1', '').replace(/\D/g, '');
}

export function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function randomDate(min: Date, max: Date): Date {
  return new Date(randomInt(min.getTime(), max.getTime()));
}

export function randomFirstName(firstNames: string[]): string {
  return firstNames[randomInt(0, firstNames.length - 1)];
}

export function getOrdinalSuffix(n: number, language: SupportedLanguageCode, isFeminine = false): string {
  if (language === 'fr') {
    return n === 1 ? (isFeminine ? 're' : 'er') : 'e';
  }

  const remainder = n % 100;
  if (remainder >= 11 && remainder <= 13) {
    return 'th';
  }

  switch (n % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

export function formatCurrency(value: number, language: SupportedLanguageCode, withDecimal: boolean): string {
  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: 'CAD',
  };

  if (withDecimal) {
    options.minimumFractionDigits = 2;
    options.maximumFractionDigits = 2;
  } else {
    options.minimumFractionDigits = 0;
    options.maximumFractionDigits = 0;
  }

  return value.toLocaleString(`${language}-CA`, options);
}

export function findClosestValue(targetValue: number, array: number[]): number | undefined {
  if (array.length === 0) return undefined;

  return array.reduce((closest, current) => {
    return Math.abs(current - targetValue) < Math.abs(closest - targetValue) ? current : closest;
  });
}
